import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { AddItemForm } from './add-item-form';
import { ItemsList } from './items-list';

interface ColumnInfoDialogProps {
  isOpen: boolean;
  onClose: () => void;
  columnType: 'department' | 'touchpoint' | 'subtouchpoint' | 'title';
  propertyTypeId: string | number;
  propertyTypeName?: string;
  onRefresh?: () => void;
}

export function ColumnInfoDialog({
  isOpen,
  onClose,
  columnType,
  propertyTypeId,
  propertyTypeName = 'Property Type',
  onRefresh,
}: ColumnInfoDialogProps) {
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  const handleAddItem = (data: Record<string, string>) => {
    // Item creation is handled by AddItemForm with mutations
    toast({
      title: t('templates.admin.columnDialog.toasts.itemAdded'),
      description: t('templates.admin.columnDialog.toasts.itemAddedMessage', {
        name: data.nameEnglish || data.name_en,
      }),
    });
  };

  const handleSuccess = () => {
    setRefreshKey(prev => prev + 1);
    onRefresh?.();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={cn(
          'max-w-4xl max-h-[90vh] overflow-hidden flex flex-col',
          isRTL && 'rtl'
        )}
      >
        <DialogHeader className={cn(isRTL && 'text-right')}>
          <DialogTitle
            className={cn(
              'flex items-center gap-2',
              isRTL ? 'flex-row-reverse' : 'flex-row'
            )}
          >
            {t('templates.admin.columnDialog.manage')} {columnType}s
          </DialogTitle>
          <DialogDescription className={cn(isRTL && 'text-right')}>
            {t('templates.admin.columnDialog.description', {
              propertyType: propertyTypeName,
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 py-4">
          {/* Add new item section */}
          <div>
            <AddItemForm
              columnType={columnType}
              propertyTypeId={propertyTypeId}
              onAdd={handleAddItem}
              onCancel={() => {}}
              onSuccess={handleSuccess}
            />
          </div>

          {/* Existing items list with infinite scroll */}
          <div>
            <ItemsList
              key={refreshKey}
              columnType={columnType}
              propertyTypeId={propertyTypeId}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              onRefresh={handleSuccess}
            />
          </div>
        </div>

        <DialogFooter className={cn(isRTL && 'flex-row-reverse')}>
          <Button onClick={onClose}>
            {t('templates.admin.columnDialog.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
